import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ShieldCheck, Star, BadgeCheck, Sparkles, Wallet, Gift, Headphones, HelpCircle, Flame, ListChecks, Download, Rocket, Heart, Trophy, Crown, ChevronRight } from "lucide-react";
import Hero, { Corners } from "../components/Hero";
import GameGrid from "../components/GameGrid";
import TelegramBanner from "../components/TelegramBanner";
import Reviews from "../components/Reviews";
import { useData } from "../data/DataContext";
import Seo from "../components/Seo";

const scrollToGames = () => {
  const el = document.getElementById("games");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const popularApps = [
  { slug: "yono-777", name: "Yono 777", desc: "slot machines plus quick table games such as Car Roulette, and one of the largest welcome offers on our list at ₹521." },
  { slug: "yono-vip", name: "Yono VIP", desc: "slots, rummy and casino tables built around a VIP ladder that adds rebates and daily rewards; new players start with ₹71." },
  { slug: "yono-rummy", name: "Yono Rummy", desc: "the rummy app that carries the Yono name itself, with OTP or password login and ₹271 for new accounts." },
  { slug: "yono-slots", name: "Yono Slots", desc: "slots only: reels, jackpots and bonus rounds without any rummy tables, plus ₹68 to begin with." },
  { slug: "yono-games", name: "Yono Games APK", desc: "the multi-game Yono Games app with slots, rummy and Teen Patti in one place and a ₹61 joining bonus." },
];

const tips = [
  { title: "Start with the game you enjoy.", desc: "Rummy players are happiest in a rummy-first app, slot fans in a slots app; all-rounders suit people who like to switch." },
  { title: "Read the bonus correctly.", desc: "A signup bonus is play money in a separate balance - only what you win can be taken out." },
  { title: "Check the payout limit.", desc: "Every app on this list lets you withdraw from ₹100 to a bank account or UPI ID in your own name." },
  { title: "Test before you trust.", desc: "Make one small withdrawal first and add more money only after it reaches you." },
];

const SectionTitle = ({ children, icon: Icon = Sparkles }) => (
  <h3 className="text-xl sm:text-2xl font-extrabold text-white mt-10 mb-3 flex items-center gap-3">
    <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#ffb224] text-[#062a13] flex items-center justify-center shadow-[0_6px_16px_-6px_rgba(255,178,36,0.8)] border-b-[3px] border-[#b47400] shrink-0">
      <Icon size={18} strokeWidth={2.5} />
    </span>
    {children}
  </h3>
);

const FEATURES = [
  { icon: ShieldCheck, title: "Verified APKs", desc: "Every link checked before listing" },
  { icon: Wallet, title: "₹100 Payout", desc: "Low minimum withdrawal on all apps" },
  { icon: Gift, title: "₹49–₹545 Bonus", desc: "Real signup bonus on each app" },
  { icon: Headphones, title: "24/7 Support", desc: "In-app chat on every Yono app" },
];

const FeatureStrip = () => (
  <div data-testid="feature-strip" className="max-w-6xl mx-auto px-4 -mt-2 mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
    {FEATURES.map(({ icon: Icon, title, desc }, i) => (
      <div
        key={title}
        className="group relative overflow-hidden bg-gradient-to-r from-[#0b4d24] via-[#073c1b] to-[#052b14] border border-[#1c5f35] border-b-[4px] border-b-[#0a3a1b] rounded-2xl px-4 py-3.5 sm:p-4 flex items-center gap-4 hover:border-[#ffb224]/60 hover:-translate-y-0.5 active:scale-[0.98] transition-all duration-200"
        style={{ animation: "fadeUp 0.5s ease both", animationDelay: `${i * 70}ms` }}
      >
        <Corners inside size="w-4 h-4" color="border-[#ffb224]/70" />
        <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 -left-full w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-[-20deg] group-hover:left-[150%] group-active:left-[150%] transition-[left] duration-700 ease-out" />
        <span className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-[#ffb224] text-[#062a13] flex items-center justify-center shrink-0 shadow-[0_8px_18px_-6px_rgba(255,178,36,0.9)] border-b-[4px] border-[#b47400] group-hover:scale-105 transition-transform">
          <Icon size={24} strokeWidth={2.4} />
        </span>
        <span className="min-w-0 flex-1">
          <b className="block text-white text-[16px] sm:text-[17px] font-extrabold leading-tight">{title}</b>
          <span className="block text-emerald-50/65 text-[12px] sm:text-[13px] leading-snug mt-1">{desc}</span>
        </span>
        <ChevronRight size={18} className="text-[#ffb224]/50 group-hover:text-[#ffb224] group-hover:translate-x-1 transition-all shrink-0 sm:hidden lg:block" />
      </div>
    ))}
  </div>
);

const Home = () => {
  const { allGames, newGames, popularSearches, settings } = useData();
  const c = settings.content || {};
  const [activeTab, setActiveTab] = useState("old");
  const [query, setQuery] = useState("");
  const location = useLocation();

  useEffect(() => {
    const q = new URLSearchParams(location.search).get("q") || "";
    setQuery(q);
    if (q) setTimeout(scrollToGames, 100);
  }, [location.search]);

  const games = activeTab === "old" ? allGames : newGames;

  const handleDiwa = () => {
    setActiveTab("old");
    setQuery("diwa");
    setTimeout(scrollToGames, 50);
  };

  return (
    <div>
      <Seo
        title={settings.metaTitle || "Maha Yono Games – Play Rummy, Ludo, Teen Patti & All Yono Games | ₹49–₹545 Bonus"}
        description={settings.metaDescription || "Maha Yono Games is the ultimate platform to discover and download the latest Yono Games (Yono Play) apps."}
        keywords={[...(settings.metaKeywords || []), ...popularSearches.map((k) => k.toLowerCase())]}
        schema={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Maha Games Apps",
          url: window.location.origin,
          potentialAction: { "@type": "SearchAction", target: `${window.location.origin}/?q={search_term_string}`, "query-input": "required name=search_term_string" },
        }}
      />
      <Hero onExplore={scrollToGames} />
      <FeatureStrip />

      {query && (
        <div className="flex justify-center my-5">
          <button
            onClick={() => setQuery("")}
            data-testid="clear-query-btn"
            className="bg-[#15562e] text-[#ffb224] text-sm font-bold px-4 py-1.5 rounded-full hover:bg-[#1c6b3a] transition-colors"
          >
            Showing: "{query}" games × Clear
          </button>
        </div>
      )}

      <GameGrid
        games={games}
        query={query}
        title={activeTab === "old" ? "All Yono Games" : "New Yono Games"}
        activeTab={activeTab}
        setActiveTab={(t) => { setActiveTab(t); setQuery(""); }}
      />

      <div className="max-w-6xl mx-auto px-4 mt-8">
        <button data-testid="hero-diwa-btn" onClick={handleDiwa} className="group relative w-full sm:w-auto bg-[#15562e] hover:bg-[#1c6b3a] text-white font-extrabold text-[15px] sm:text-base px-5 sm:px-7 py-2.5 sm:py-3.5 rounded-xl sm:rounded-[14px] border-b-[4px] sm:border-b-[5px] border-[#0a3a1b] active:border-b-[1px] active:translate-y-[4px] transition-all">
          <span aria-hidden="true" className="pointer-events-none absolute -top-1.5 -left-1.5 w-5 h-5 border-t-2 border-l-2 rounded-tl-md border-[#ffb224]/60" />
          <span aria-hidden="true" className="pointer-events-none absolute -bottom-1.5 -right-1.5 w-5 h-5 border-b-2 border-r-2 rounded-br-md border-[#ffb224]/60" />
          All Diwa Games
        </button>
      </div>

      <Reviews />

      <TelegramBanner />

      <section className="max-w-4xl mx-auto px-4 pb-4">
        <h2 className="text-2xl sm:text-3xl font-extrabold text-white text-center">
          {c.introTitle || "All Yono Games, Checked and Listed in One Place"}
        </h2>
        <p className="text-emerald-50/70 leading-relaxed mt-4 text-[15px]">
          {c.introText}</p>

        <SectionTitle icon={HelpCircle}>What counts as a Yono game?</SectionTitle>
        <p className="text-emerald-50/70 leading-relaxed text-[15px]">
          A Yono game is an Android app from the Yono family of real-money games. These apps share a familiar setup - you log in with your mobile number, one wallet covers every table, and the lobby mixes rummy, Teen Patti, slots and short rounds such as crash games or 7 Up Down. Yono games are handed out as APK files rather than through the Play Store, which is exactly why a list of all Yono games that is checked and kept current saves you time and trouble.
        </p>

        <SectionTitle icon={Flame}>Popular Yono apps you will find here</SectionTitle>
        <div className="grid gap-3">
          {popularApps.map((app) => (
            <Link
              key={app.slug}
              to={`/game/${app.slug}`}
              className="flex gap-3 items-start bg-[#073c1b] border border-[#1c5f35] rounded-2xl p-4 hover:border-[#ffb224]/60 transition-all duration-200"
            >
              <Star size={18} className="text-[#ffb224] shrink-0 mt-1" fill="currentColor" />
              <p className="text-[15px] text-emerald-50/70 leading-relaxed">
                <span className="font-bold text-[#ffb224]">{app.name}</span> - {app.desc}
              </p>
            </Link>
          ))}
        </div>

        <SectionTitle icon={ListChecks}>How to pick the right Yono game</SectionTitle>
        <div className="grid sm:grid-cols-2 gap-3">
          {tips.map((tip) => (
            <div key={tip.title} className="flex gap-3 items-start bg-[#073c1b] border border-[#1c5f35] rounded-2xl p-4">
              <CheckCircle2 size={18} className="text-[#ffb224] shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-50/80 leading-relaxed">
                <span className="font-bold text-white">{tip.title}</span> {tip.desc}
              </p>
            </div>
          ))}
        </div>

        <SectionTitle icon={Download}>Downloading Yono games safely</SectionTitle>
        <div className="flex gap-3 items-start bg-[#073c1b] border border-[#1c5f35] rounded-2xl p-4">
          <ShieldCheck size={20} className="text-[#ffb224] shrink-0 mt-0.5" />
          <p className="text-emerald-50/70 leading-relaxed text-[15px]">
            Each app page on Maha Games Apps links to that app's download, shows how to allow the install on Android and lists the minimum phone it needs. Skip "mod" or "hack" versions shared in chats, never give your OTP or UPI PIN to anyone, and treat any request for a fee to "unlock" a withdrawal as a scam.
          </p>
        </div>

        <SectionTitle icon={Rocket}>New launches and long-time favourites</SectionTitle>
        <div data-testid="new-launches-section" className="flex gap-3 items-start bg-[#073c1b] border border-[#1c5f35] rounded-2xl p-4">
          <Sparkles size={20} className="text-[#ffb224] shrink-0 mt-0.5" />
          <p className="text-emerald-50/70 leading-relaxed text-[15px]">
            Fresh Yono games show up first on our{" "}
            <button onClick={() => { setActiveTab("new"); setQuery(""); setTimeout(scrollToGames, 50); }} className="text-[#ffb224] font-semibold underline">New Games</button>{" "}
            page, while apps that have been around for a while stay on{" "}
            <button onClick={() => { setActiveTab("old"); setQuery(""); setTimeout(scrollToGames, 50); }} className="text-[#ffb224] font-semibold underline">Other Games</button>.
            For game types, bonus ranges and a longer safety checklist, read the complete guide on our{" "}
            <Link to="/about" className="text-[#ffb224] font-semibold underline">list page</Link>.
          </p>
        </div>

        <SectionTitle icon={Heart}>Play within your limits</SectionTitle>
        <div className="flex gap-3 items-start bg-[#073c1b] border border-[#1c5f35] rounded-2xl p-4">
          <BadgeCheck size={20} className="text-[#ffb224] shrink-0 mt-0.5" />
          <p className="text-emerald-50/70 leading-relaxed text-[15px]">
            Every app in our All Yono Games list involves real money and is meant for adults aged 18 and over. Set a budget before you open any app, take regular breaks and stop when you reach your limit.
          </p>
        </div>

        <div data-testid="home-seo-section" className="mt-12 bg-[#073c1b] border border-[#1c5f35] rounded-3xl p-5 sm:p-7 ">
          <h2 className="text-xl sm:text-2xl font-extrabold text-white leading-snug">
            {c.mahaTitle}
          </h2>
          <p className="text-emerald-50/70 leading-relaxed mt-4 text-[15px]">
            {c.mahaText}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {["Maha Yono Games", "Yono Games Download", "Yono Rummy", "Yono Ludo", "Yono Teen Patti", "Yono Play APK", "Yono Games Bonus", "Yono Fast Withdrawal", "All Yono Apps 2026"].map((t) => (
              <span key={t} className="text-xs font-semibold bg-[#073c1b] text-[#ffb224] border border-[#1c5f35] px-3 py-1.5 rounded-full">{t}</span>
            ))}
          </div>
        </div>

        <SectionTitle icon={Trophy}>Why Yono Games?</SectionTitle>
        <div data-testid="home-why-yono" className="space-y-3 text-emerald-50/70 leading-relaxed text-[15px]">
          <p>
            If you're looking for one trusted place to enjoy a wide variety of online games, <span className="font-bold text-white">Yono Games</span> stands out as a complete gaming destination. Instead of downloading multiple apps, Yono Games brings together popular game variants under one platform. From Rummy, Roulette, Aviator, and Spin games to Slots, Andar Bahar, Wheel of Fortune, Ganesha Gold, Triple 777, Power of the Kraken, and many more - there's always something new to explore. The platform is designed for smooth gameplay, regular updates, and exciting features that keep players engaged.
          </p>
          <p>
            What makes Yono Games even more appealing is the extra value it offers to players. Features like weekly and monthly cards add more flexibility, while options such as the gullak help users collect additional rewards over time. On our website, you'll find a carefully curated list of all officially released Yono Games apps. We only list games that are publicly announced and launched by the Yono Games team (now Yono Play), so you can download with full confidence. By choosing apps from our site, players can also receive a welcome bonus of up to <span className="font-bold text-[#ffb224]">2500 points</span>, along with additional rewards as they move up VIP levels.
          </p>
          <p>
            Safety and authenticity matter when downloading gaming apps. Although many websites claim to offer Yono Games, not all sources are reliable. Unofficial APKs may include trackers or unwanted code. That's why downloading from trusted and official listings is always recommended. Beyond gameplay, Yono Games also offers a referral system that lets you earn bonuses by inviting friends. Each user gets a unique referral link - share it, invite others, and enjoy extra rewards as your friends join and play. All these reasons together make Yono Games a preferred choice for players who value variety, security, and rewarding experiences.
          </p>
        </div>

        <SectionTitle icon={Crown}>Why Choose Maha Yono Games?</SectionTitle>
        <div data-testid="home-why-maha" className="flex gap-3 items-start bg-[#073c1b] border border-[#1c5f35] rounded-2xl p-5">
          <BadgeCheck size={22} className="text-[#ffb224] shrink-0 mt-0.5" />
          <p className="text-emerald-50/80 leading-relaxed text-[15px]">
            {c.whyMahaText}
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
